import React from "react";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { fullName } from "utils/fullName";
import { CommentUser } from "./types";

// The comment's own size, so a name never shrinks mid-sentence.
const MENTION_SX = {
  font: "inherit",
  fontWeight: "var(--type-weight-medium)",
  color: "var(--color-text-brand)",
};

export const maskMentions = (text: string, users: CommentUser[]) => {
  return text.replace(/@\[(.*?)\]/g, (match, userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? `@${fullName(user)}` : match;
  });
};

export const reverseMaskMentions = (
  text: string,
  insertedMentions: Map<string, string>,
) => {
  let result = text;
  insertedMentions.forEach((displayName, userId) => {
    // Escape special regex characters in names
    const escapedName = `@${displayName}`.replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&",
    );
    // Match display name only if it's not part of a longer word
    const regex = new RegExp(
      `(?<=^|[\\s.,;:!?()\\[\\]{}])${escapedName}(?=[\\s.,;:!?()\\[\\]{}]|$)`,
      "g",
    );
    result = result.replace(regex, `@[${userId}]`);
  });
  return result;
};

/** With `onMentionClick`, a mention is a button; without it, highlighted but inert text. */
export function renderCommentWithMentions(
  comment: string,
  mentions: CommentUser[],
  onMentionClick?: (userId: string) => void,
) {
  const regex = /@\[(.+?)\]/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(comment)) !== null) {
    if (match.index > lastIndex) {
      parts.push(comment.slice(lastIndex, match.index));
    }
    const userId = match[1];
    const user = mentions.find((u) => u.id === userId);
    if (user) {
      const key = userId + match.index;
      const label = `@${fullName(user)}`;
      parts.push(
        onMentionClick ? (
          <Link
            key={key}
            component="button"
            type="button"
            underline="hover"
            onClick={() => onMentionClick(userId)}
            sx={{
              ...MENTION_SX,
              // Undo the button reset's middle alignment and unselectable text.
              verticalAlign: "baseline",
              userSelect: "text",
            }}
          >
            {label}
          </Link>
        ) : (
          <Typography
            key={key}
            component="span"
            variant="inherit"
            sx={MENTION_SX}
          >
            {label}
          </Typography>
        ),
      );
    } else {
      parts.push(match[0]);
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < comment.length) {
    parts.push(comment.slice(lastIndex));
  }
  return parts;
}

export const validateComment = (comment: string): string | null => {
  const unmatched = comment.match(/@\[(?![^\]]*\])/g);
  if (unmatched) {
    return "Invalid mention format detected. Please remove incomplete mentions.";
  }

  if (comment.includes("@[]")) {
    return "Empty mention detected. Please remove empty mentions.";
  }

  return null;
};
